import {
    type CSSProperties,
    useEffect,
    useState
} from "react";
import {
    StyleSheet
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    StatTile,
    Button
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    UserIcon
} from "../../anatomy/icons";

const NAME = "StatTile";

const USAGE_CODE = `<StatTile
    trend={[42, 45, 41, 52, 49, 57]}
    isTrendFlush={true}
>
    <StatTile.Title
        icon={UserIcon}
    >
        Active users
    </StatTile.Title>
    <StatTile.Value>
        1,284
    </StatTile.Value>
    <StatTile.Delta
        description="vs previous 30 days"
        value={0.124}
    />
</StatTile>`;

const BOARD_CODE = `<StatTile
    isTrendFlush={true}
    trend={revenueByMonth}
>
    <StatTile.Title>
        Gelir
    </StatTile.Title>
    <StatTile.Value>
        ₺4,2 Mn
    </StatTile.Value>
    <StatTile.Delta
        description="geçen aya göre"
        value={0.125}
    />
</StatTile>`;

const DELTA_CODE = `<StatTile.Delta
    value={0.124}
/>

<StatTile.Delta
    isIncreaseGood={false}
    unit="point"
    value={-0.4}
/>`;

const VARIANTS = [
    "outline",
    "elevated",
    "filled"
] as const;

const USERS_TREND = [
    42,
    45,
    41,
    52,
    49,
    57
];

const SESSIONS_TREND = [
    30,
    34,
    33,
    38,
    41,
    44,
    47
];

const RETURNS_TREND = [
    2.9,
    2.8,
    2.9,
    2.6,
    2.4,
    2.3,
    2.1
];

const PERCENT_OPTIONS: Intl.NumberFormatOptions = {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    style: "percent"
};

const BOARD_TILES = [
    {
        titleKey: "statTilePage.revenue",
        isIncreaseGood: true,
        unit: "percent",
        key: "revenue",
        delta: 0.125,
        trend: [
            3.12,
            3.28,
            3.21,
            3.46,
            3.39,
            3.58,
            3.52,
            3.81,
            3.69,
            3.92,
            3.734,
            4.2
        ]
    },
    {
        titleKey: "statTilePage.orders",
        isIncreaseGood: true,
        unit: "percent",
        key: "orders",
        delta: 0.081,
        trend: [
            982,
            1014,
            1046,
            991,
            1083,
            1122,
            1097,
            1151,
            1169,
            1212,
            1188,
            1284
        ]
    },
    {
        titleKey: "statTilePage.conversion",
        isIncreaseGood: true,
        key: "conversion",
        unit: "point",
        delta: -0.4,
        trend: [
            3.1,
            3.3,
            3.4,
            3.2,
            3.5,
            3.6,
            3.4,
            3.7,
            3.5,
            3.8,
            3.6,
            3.2
        ]
    },
    {
        titleKey: "statTilePage.responseTime",
        isIncreaseGood: false,
        key: "responseTime",
        unit: "percent",
        delta: -0.177,
        trend: [
            148,
            141,
            152,
            139,
            133,
            127,
            131,
            124,
            118,
            126,
            124,
            102
        ]
    }
] as const;

const INITIAL_USERS = 1284;

const USERS_STEP = 36;

const REFRESH_DELAY = 1200;

const ACTIONS_STYLE: CSSProperties = {
    alignItems: "center",
    display: "flex",
    gap: 8
};

const stylesheet = StyleSheet.create({
    tile: {
        width: 240
    }
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("statTilePage.boardTitle"),
        id: "board"
    },
    {
        title: t("statTilePage.trendTitle"),
        id: "trend"
    },
    {
        title: t("statTilePage.deltaTitle"),
        id: "delta"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const StatTilePage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const [
        users,
        setUsers
    ] = useState(INITIAL_USERS);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const [
        openCount,
        setOpenCount
    ] = useState(0);

    useEffect(() => {
        if(!isRefreshing) {
            return;
        }

        const timer = window.setTimeout(() => {
            setUsers((current) => current + USERS_STEP);

            setIsRefreshing(false);
        }, REFRESH_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isRefreshing
    ]);

    const formatNumber = (value: number) => value.toLocaleString(language);

    const formatPercent = (value: number) => value.toLocaleString(language, PERCENT_OPTIONS);

    const handleRefresh = () => setIsRefreshing(true);

    const handleUpdate = () => setUsers((current) => current + USERS_STEP);

    const handleOpen = () => setOpenCount((current) => current + 1);

    const formatBoardValue = (key: string) => {
        if(key === "revenue") {
            return t("statTilePage.revenueValue", [
                (4.2).toLocaleString(language, {
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 1
                })
            ]);
        }

        if(key === "orders") {
            return formatNumber(1284);
        }

        if(key === "conversion") {
            return t("statTilePage.percentValue", [
                (3.2).toLocaleString(language, {
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 1
                })
            ]);
        }

        return t("statTilePage.durationValue", [
            1,
            42
        ]);
    };

    return <Article>
        <ComponentHeader
            lead={t("statTilePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("statTilePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("statTilePage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("statTilePage.overviewCaption")}
                isStacked={true}
            >
                <StatTile
                    spreadBehaviour="stretch"
                    isLoading={isRefreshing}
                    trend={USERS_TREND}
                    isTrendFlush={true}
                >
                    <StatTile.Title
                        icon={UserIcon}
                    >
                        {t("sample.metric")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(users)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("statTilePage.vsPrevious")}
                        value={0.124}
                    />
                </StatTile>
                <div
                    style={ACTIONS_STYLE}
                >
                    <Button
                        title={t("statTilePage.refresh")}
                        isDisabled={isRefreshing}
                        onPress={handleRefresh}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                    <Button
                        title={t("statTilePage.update")}
                        isDisabled={isRefreshing}
                        onPress={handleUpdate}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("statTilePage.boardTitle")}
            id="board"
        >
            <Paragraph>
                {t("statTilePage.boardText")}
            </Paragraph>
            <Preview
                caption={t("statTilePage.boardCaption")}
            >
                {
                    BOARD_TILES.map((tile) => <StatTile
                        style={stylesheet.tile}
                        isTrendFlush={true}
                        trend={[
                            ...tile.trend
                        ]}
                        key={tile.key}
                    >
                        <StatTile.Title>
                            {t(tile.titleKey)}
                        </StatTile.Title>
                        <StatTile.Value>
                            {formatBoardValue(tile.key)}
                        </StatTile.Value>
                        <StatTile.Delta
                            description={t("statTilePage.vsLastMonth")}
                            isIncreaseGood={tile.isIncreaseGood}
                            value={tile.delta}
                            unit={tile.unit}
                        />
                    </StatTile>)
                }
            </Preview>
            <Callout>
                {t("statTilePage.boardNote")}
            </Callout>
            <CodeBlock
                code={BOARD_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("statTilePage.trendTitle")}
            id="trend"
        >
            <Paragraph>
                {t("statTilePage.trendText")}
            </Paragraph>
            <Preview>
                <StatTile
                    style={stylesheet.tile}
                    trend={SESSIONS_TREND}
                    isTrendFlush={true}
                >
                    <StatTile.Title>
                        {t("statTilePage.sessions")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(48120)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description="isTrendFlush"
                        value={0.082}
                    />
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                    trend={RETURNS_TREND}
                    trendStep={5}
                >
                    <StatTile.Title>
                        {t("statTilePage.returnRate")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatPercent(0.021)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description="trendStep={5}"
                        isIncreaseGood={false}
                        unit="point"
                        value={-0.8}
                    />
                </StatTile>
            </Preview>
        </Section>
        <Section
            title={t("statTilePage.deltaTitle")}
            id="delta"
        >
            <Paragraph>
                {t("statTilePage.deltaText")}
            </Paragraph>
            <Preview>
                <StatTile
                    style={stylesheet.tile}
                >
                    <StatTile.Title>
                        {t("statTilePage.orders")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(6284)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("statTilePage.increaseGood")}
                        value={0.081}
                    />
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                >
                    <StatTile.Title>
                        {t("statTilePage.sessions")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(48120)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("statTilePage.decreaseBad")}
                        value={-0.042}
                    />
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                >
                    <StatTile.Title>
                        {t("statTilePage.returnRate")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatPercent(0.021)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("statTilePage.decreaseGood")}
                        isIncreaseGood={false}
                        unit="point"
                        value={-0.4}
                    />
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                >
                    <StatTile.Title>
                        {t("statTilePage.satisfaction")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatPercent(0.92)}
                    </StatTile.Value>
                    <StatTile.Delta
                        description={t("statTilePage.unchanged")}
                        value={0}
                    />
                </StatTile>
            </Preview>
            <CodeBlock
                code={DELTA_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("statTilePage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => <StatTile
                        style={stylesheet.tile}
                        variant={variant}
                        key={variant}
                    >
                        <StatTile.Title>
                            {`variant="${variant}"`}
                        </StatTile.Title>
                        <StatTile.Value>
                            {formatNumber(1284)}
                        </StatTile.Value>
                        <StatTile.Delta
                            value={0.124}
                        />
                    </StatTile>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("statTilePage.statesText")}
            </Paragraph>
            <Preview
                caption={t("statTilePage.statesCaption", [
                    openCount
                ])}
            >
                <StatTile
                    accessibilityLabel={t("statTilePage.openOrders")}
                    style={stylesheet.tile}
                    onPress={handleOpen}
                    isPressable={true}
                >
                    <StatTile.Title>
                        {t("statTilePage.orders")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(6284)}
                    </StatTile.Value>
                    <StatTile.Delta
                        value={0.081}
                    />
                </StatTile>
                <StatTile
                    accessibilityLabel={t("statTilePage.openOrders")}
                    style={stylesheet.tile}
                    onPress={handleOpen}
                    isPressable={true}
                    isDisabled={true}
                >
                    <StatTile.Title>
                        {t("statTilePage.orders")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(6284)}
                    </StatTile.Value>
                    <StatTile.Delta
                        value={0.081}
                    />
                </StatTile>
                <StatTile
                    style={stylesheet.tile}
                    isLoading={true}
                >
                    <StatTile.Title>
                        {t("statTilePage.orders")}
                    </StatTile.Title>
                    <StatTile.Value>
                        {formatNumber(6284)}
                    </StatTile.Value>
                    <StatTile.Delta
                        value={0.081}
                    />
                </StatTile>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("statTilePage.a11yPlain"),
                    t("statTilePage.a11yDelta"),
                    t("statTilePage.a11yTrend"),
                    t("statTilePage.a11yLoading"),
                    t("statTilePage.a11yPress"),
                    t("statTilePage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("statTilePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("statTilePage.propTrend"),
                        type: "Array<number>",
                        name: "trend"
                    },
                    {
                        description: t("statTilePage.propTrendStep"),
                        name: "trendStep",
                        type: "4 | 5"
                    },
                    {
                        description: t("statTilePage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("statTilePage.propIsTrendFlush"),
                        defaultValue: "false",
                        name: "isTrendFlush",
                        type: "boolean"
                    },
                    {
                        type: "\"outline\" | \"elevated\" | \"filled\"",
                        description: t("statTilePage.propVariant"),
                        defaultValue: "\"outline\"",
                        name: "variant"
                    },
                    {
                        description: t("statTilePage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("statTilePage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("statTilePage.propIsPressable"),
                        defaultValue: "false",
                        name: "isPressable",
                        type: "boolean"
                    },
                    {
                        description: t("statTilePage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("statTilePage.propHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("statTilePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("statTilePage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("statTilePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("statTilePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("statTilePage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("statTilePage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("statTilePage.propPartIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("statTilePage.propPartValue"),
                        name: "value",
                        type: "number"
                    },
                    {
                        description: t("statTilePage.propPartUnit"),
                        type: "\"percent\" | \"point\"",
                        defaultValue: "\"percent\"",
                        name: "unit"
                    },
                    {
                        description: t("statTilePage.propPartIsIncreaseGood"),
                        name: "isIncreaseGood",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("statTilePage.propPartDescription"),
                        name: "description",
                        type: "string"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default StatTilePage;
